import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Locale } from 'date-fns';
import { getDateLocale } from 'utils/data/language/getDateLocale';
import { enUS } from 'date-fns/locale/en-US';

const DateLocaleContext = createContext<Locale>(enUS);

export const useGlobalDateLocale = () => {
  const localeContext = useContext(DateLocaleContext);
  if (!localeContext) {
    throw new Error(
      'useGlobalDateLocale must be used inside <DateLocaleProvider>',
    );
  }
  const RTL_LANGUAGES = ['ar'];
  const isRTL = RTL_LANGUAGES.some((lang) => {
    const language = localeContext.code?.toLowerCase();
    return language === lang || language?.startsWith(`${lang}-`);
  });
  return {
    isRTL,
    locale: localeContext,
  };
};

interface DateLocaleProviderProps {
  children: React.ReactNode;
}

export const DateLocaleProvider = ({ children }: DateLocaleProviderProps) => {
  const { i18n: currentI18n } = useTranslation();
  const [locale, setLocale] = useState<Locale>(enUS);

  useEffect(() => {
    let languageSet = false;
    getDateLocale(currentI18n.language).then((nextLocale) => {
      if (!languageSet) setLocale(nextLocale);
    });
    return () => {
      languageSet = true;
    };
  }, [currentI18n.language]);

  return (
    <DateLocaleContext.Provider value={locale}>
      {children}
    </DateLocaleContext.Provider>
  );
};

export default DateLocaleContext;
